/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * @author Gladson Samuel S
 * CourseHeaderStyles.js
 * Styles for difficult topic components
 */

import { styled } from '@mui/material/styles';
import { Button, IconButton, MenuItem, TextField, Typography } from '@mui/material';
import Paper from '@mui/material/Paper';
import MenuList from '@mui/material/MenuList';
import CustomCard from '../../common/CustomCard';
import Icon from '../../icons/Icon';
import mixins from '../../../assets/styles/mixins.scss';
import CommonStyles from '../../../assets/styles/CommonStyles';

export const CourseHeaderContainer = styled('div', {
  shouldForwardProp: (prop) => prop !== 'twoColumns'
})(({ twoColumns }) => ({
  '&&': {
    display: 'flex',
    flexDirection: 'column',
    gap: twoColumns ? 16 : 12,

    '& > *': {
      boxSizing: 'border-box'
    },
    [mixins['md-sm-min']]: {
      flexDirection: 'row'
    },
    ...(twoColumns ? {
      backgroundColor: mixins.lightThemeBgColor2,
      padding: 16,
      borderRadius: 24,
      boxSizing: 'border-box',

      [mixins['mlg-min']]: {
        flexDirection: 'row',
        alignItems: 'stretch'
      },

      '& > *:last-child': {
        flex: '1 0 0',
        width: '100%',
        backgroundColor: `${mixins.chalkWhiteFont} !important`,
        borderRadius: '16px !important',
        padding: '12px !important',
        flexDirection: 'column',
        gap: 8,
        alignItems: 'flex-start',

        [mixins['md-sm-min']]: {
          flexDirection: 'row'
        },

        [mixins['mlg-min']]: {
          flex: '1 1 0',
          minWidth: 0,
          width: 'auto'
        },

        '& > div:first-child': {
          [mixins['mlg-min']]: {
            minWidth: '128.25px',
            width: '128.25px',
            height: '171px',
            maxHeight: '171px'
          },
          [mixins['lg-min']]: {
            minWidth: '135px',
            width: '135px',
            height: '180px',
            maxHeight: '180px'
          },

          '& img': {
            [mixins['mlg-min']]: {
              width: '128.25px',
              height: '171px'
            },
            [mixins['lg-min']]: {
              width: '135px',
              height: '180px'
            }
          }
        }
      }
    } : {})
  }
}));

export const CourseHeaderCard = styled(CustomCard)({
  '&&': {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 4,
    flex: '1 0 0',
    alignSelf: 'stretch',
    borderRadius: '12px',
    backgroundColor: mixins.chalkWhiteFont
  }
});

export const CourseHeaderCourseCard = styled(CourseHeaderCard)({
  '&&': {
    backgroundColor: mixins.lightThemeBgColor2,
    borderRadius: 24
  }
});

export const CourseHeaderCourseCardTwoColumns = styled(CourseHeaderCard)({
  '&&': {
    backgroundColor: mixins.chalkWhiteFont,
    borderRadius: 16,
    padding: 12,
    gap: 12,
    flexDirection: 'column',
    justifyContent: 'space-between',
    flex: '1 0 0',
    width: '100%',

    [mixins['mlg-min']]: {
      flex: '0 0 416px',
      maxWidth: '416px'
    }
  }
});

export const CourseHeaderCourseCardContent = styled('div')({
  '&&': {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    width: '100%',
    flex: '1 0 0',
    padding: 4,

    '& > .MuiTypography-root': {
      fontSize: '24px',
      lineHeight: '32px',
      letterSpacing: '-0.48px',

      [mixins['mlg-min']]: {
        fontSize: '24px',
        lineHeight: '32px',
        letterSpacing: '-0.48px'
      },
      [mixins['lg-min']]: {
        fontSize: '24px',
        lineHeight: '32px',
        letterSpacing: '-0.48px'
      }
    }
  }
});

export const CourseHeaderMetaDataContainer = styled('div')({
  '&&': {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 4,
    alignItems: 'center',
    width: '100%'
  }
});

export const CourseHeaderMetaDataItem = styled('div')({
  '&&': {
    display: 'inline-flex',
    alignItems: 'center',
    padding: 4,
    borderRadius: 4,
    border: '1px solid rgba(13, 0, 77, 0.45)',
    backgroundColor: mixins.chalkWhiteFont,
    whiteSpace: 'nowrap'
  }
});

export const CourseHeaderMetaDataLabel = styled('span')({
  '&&': {
    color: mixins.color_text_primary,
    fontFeatureSettings: '\'liga\' off',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize14,
    fontStyle: 'normal',
    fontWeight: mixins.fontWeight400,
    lineHeight: '22px'
  }
});

export const CourseHeaderMetaDataValue = styled('span')({
  '&&': {
    color: mixins.color_text_primary,
    fontFeatureSettings: '\'liga\' off',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize14,
    fontStyle: 'normal',
    fontWeight: mixins.fontWeight600,
    lineHeight: '22px'
  }
});

export const CourseHeaderInviteStudentsButton = styled(Button)({
  '&&': {
    color: mixins.amethyst,
    fontFeatureSettings: '\'liga\' off',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize16,
    fontStyle: 'normal',
    fontWeight: mixins.fontWeight600,
    lineHeight: '24px',
    textTransform: 'none',
    padding: 0,
    minWidth: 'unset',
    backgroundColor: 'transparent',

    '&:hover': {
      backgroundColor: 'transparent',
      color: mixins.linkHoverColor,
      textDecoration: 'underline'
    }
  }
});

export const CourseHeaderCourseChildrenContent = styled('div')({
  '&&': {
    marginTop: 16,
    width: '100%'
  }
});

export const CourseHeaderCourseTitleContainer = styled('div')({
  '&&': {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
    alignItems: 'baseline',
    gap: 4,

    [mixins['md-sm-min']]: {
      alignItems: 'flex-start'
    }
  }
});

export const CourseHeaderCourseTitle = styled(Typography)({
  '&&': {
    color: mixins.color_text_primary,
    fontFeatureSettings: '\'liga\' off',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: '24px',
    fontStyle: 'normal',
    fontWeight: mixins.fontWeight500,
    lineHeight: '30px',
    wordBreak: 'break-word',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    [mixins['mlg-min']]: {
      fontSize: '28px',
      lineHeight: '35px',
      letterSpacing: '-0.54px'
    },
    [mixins['lg-min']]: {
      fontSize: '32px',
      lineHeight: '40px',
      letterSpacing: '-0.64px'
    }
  }
});

export const CourseHeaderBookTitle = styled(Typography)({
  '&&': {
    color: mixins.color_text_primary,
    fontFeatureSettings: '\'liga\' off',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: '20px',
    fontStyle: 'normal',
    fontWeight: mixins.fontWeight500,
    lineHeight: '27px',

    [mixins['mlg-min']]: {
      fontSize: '24px',
      lineHeight: '29px',
      letterSpacing: '-0.4px'
    },
    [mixins['xl-min']]: {
      fontSize: '24px',
      lineHeight: '32px',
      letterSpacing: '-0.4px'
    }
  }
});

export const CourseHeaderInstructorContainer = styled('div')({
  '&&': {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  }
});

export const CourseHeaderAvatar = styled('div')({
  '&&': {
    display: 'flex',
    width: '40px',
    height: '40px',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: '50%',
    color: mixins.color_text_primary,
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize14,
    fontStyle: 'normal',
    fontWeight: mixins.fontWeight500,
    lineHeight: '22px',

    '&:has(span)': {
      border: `2px solid ${mixins.navyColor}`
    },
    '& svg': {
      height: '100%',
      width: '100%',
      color: mixins.navyColor
    }
  }
});

export const CourseHeaderAvatarImage = styled('img')({
  '&&': {
    display: 'flex',
    width: '40px',
    height: '40px',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: '50%',
    color: mixins.color_text_primary,
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize14,
    fontStyle: 'normal',
    fontWeight: mixins.fontWeight500,
    lineHeight: '22px'
  }
});

export const CourseHeaderInstructorName = styled(Typography)({
  '&&': {
    color: mixins.color_text_primary,
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize14,
    fontStyle: 'normal',
    fontWeight: mixins.fontWeight500,
    lineHeight: '22px'
  }
});

export const CourseHeaderInfoContainer = styled('div')({
  '&&': {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
    flexGrow: 1,
    alignItems: 'flex-end',
    marginTop: 4
  }
});

export const CourseHeaderActionsContainer = styled('div')({
  '&&': {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',

    [mixins['md-sm-min']]: {
      width: 'auto'
    }
  }
});

export const CourseHeaderSecondaryButton = styled(Button)({
  '&&': {
    ...CommonStyles().secondaryButton,
    ...CommonStyles().secondaryButtonText,
    ...CommonStyles().focusVisible,
    maxWidth: '100%',
    width: '100%',
    justifyContent: 'center',
    padding: '8px 16px 10px 16px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',

    [mixins['md-sm-min']]: {
      maxWidth: '145px'
    }
  }
});

export const CourseHeaderSecondaryGuidesButton = styled(Button)({
  '&&': {
    ...CommonStyles().secondaryButton,
    ...CommonStyles().secondaryButtonText,
    ...CommonStyles().focusVisible,
    border: 'none',
    padding: 0,
    height: 'inherit',
    maxHeight: '22px',
    fontWeight: 600,
    color: mixins.amethyst,
    gap: 0,

    '&:hover': {
      background: 'none',
      color: mixins.linkHoverColor
    },
    [mixins['md-sm-min']]: {
      flexGrow: '1',
      alignSelf: 'center'
    }
  }
});

export const CourseHeaderIconButton = styled(IconButton)({
  '&&': {
    ...CommonStyles().iconButton,
    border: '1px solid rgba(13, 0, 77, 0.15)',
    position: 'relative'
  }
});

export const CourseHeaderUnifiedCoverInfoButton = styled(IconButton)({
  '&&': {
    position: 'absolute',
    right: -12,
    bottom: -12,
    width: 24,
    height: 24,
    minWidth: 24,
    padding: 4,
    borderRadius: '50%',
    backgroundColor: mixins.chalkWhiteFont,
    border: '0.556px solid rgba(0, 0, 0, 0.10)',
    boxShadow: '0px 4px 3px rgba(0, 0, 0, 0.10), 0px 2px 2px rgba(0, 0, 0, 0.10)',
    zIndex: 1,
    color: mixins.navyColor,

    '&:hover': {
      backgroundColor: mixins.chalkWhiteFont
    },

    '& svg, & .MuiSvgIcon-root': {
      width: 16,
      height: 16,
      fontSize: 16
    }
  }
});

export const CourseHeaderHotspot = styled('div')({
  '&&': {
    width: 6,
    height: 6,
    backgroundColor: mixins.accentHotspotColor,
    position: 'absolute',
    top: 0,
    right: 5,
    borderRadius: '50%'
  }
});

export const CourseHeaderBookCard = styled(CourseHeaderCard)({
  '&&': {
    flexDirection: 'row',
    padding: '8px !important',
    gap: 12,
    borderRadius: 16,

    [mixins['md-sm-min']]: {
      padding: '12px !important'
    }
  }
});

export const CourseHeaderCoverImageContainer = styled('div')({
  '&&': {
    minWidth: '108px',
    maxHeight: '144px',

    [mixins['md-sm-min']]: {
      minWidth: '109.5px',
      maxHeight: '146px'
    },
    [mixins['mlg-min']]: {
      minWidth: '90.75px',
      maxHeight: '121px'
    },
    [mixins['lg-min']]: {
      minWidth: '93px',
      maxHeight: '124px'
    }
  }
});

export const CourseHeaderCoverImage = styled('img')({
  '&&': {
    width: '108px',
    height: '144px',
    flexShrink: '0',
    aspectRatio: '3/4',
    objectFit: 'cover',
    borderRadius: 8,

    [mixins['md-sm-min']]: {
      width: '109.5px',
      height: '146px'
    },
    [mixins['mlg-min']]: {
      width: '90.75px',
      height: '121px'
    },
    [mixins['lg-min']]: {
      width: '93px',
      height: '124px'
    }
  }
});

export const CourseHeaderDownloadRoot = styled('div')({
  '&&': {
    width: '108px',
    height: '144px',
    flexShrink: '0',
    aspectRatio: '3/4',
    objectFit: 'cover',
    borderRadius: 8,

    [mixins['md-sm-min']]: {
      width: '109.5px',
      height: '146px'
    },
    [mixins['mlg-min']]: {
      width: '90.75px',
      height: '121px'
    },
    [mixins['lg-min']]: {
      width: '93px',
      height: '124px'
    }
  }
});

export const CourseHeaderAuthor = styled(Typography)({
  '&&': {
    color: mixins.zambezi,
    fontFeatureSettings: '\'liga\' off',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize14,
    fontStyle: 'normal',
    fontWeight: mixins.fontWeight400,
    lineHeight: '22px',
    marginBottom: 8,

    [mixins['mlg-min']]: {
      marginBottom: 0
    }
  }
});

export const CourseHeaderBookInfo = styled('div')({
  '&&': {
    display: 'flex',
    padding: '4px',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 2,
    flex: '1 0 0',
    alignSelf: 'stretch'
  }
});

export const CourseHeaderGoToPageContainer = styled('div')({
  '&&': {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    width: '100%',

    '& .MuiInputBase-input::placeholder': {
      fontSize: 10
    },

    [mixins['md-sm-min']]: {
      flexDirection: 'row',
      alignItems: 'center',
      maxHeight: 40,
      gap: '8px',
      width: 'auto'
    }
  }
});

export const CourseHeaderGoToPageLabel = styled(Typography)({
  '&&': {
    color: mixins.zambezi,
    fontFeatureSettings: '\'liga\' off',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: 12,
    fontStyle: 'normal',
    fontWeight: mixins.fontWeight400,
    lineHeight: '20px'
  }
});

export const CourseHeaderGoToTextField = styled(TextField)({
  '&&': {
    maxHeight: 40,
    width: '100%',
    padding: '8px 12px 8px 12px',
    border: `1px solid ${mixins.amethyst}`,
    borderRadius: 4,
    backgroundColor: mixins.chalkWhiteFont,
    boxSizing: 'border-box',
    maxWidth: '100%',

    '& .MuiInputBase-root': {
      width: '100%',
      color: mixins.greyScaleColor,
      fontFeatureSettings: '\'liga\' off',
      fontFamily: mixins.fontFamilyPlusSans,
      fontSize: mixins.fontSize16,
      fontStyle: 'normal',
      fontWeight: mixins.fontWeight400,
      lineHeight: '24px',

      '&::before, &::after': {
        display: 'none'
      }
    },
    '& .MuiInputBase-input': {
      padding: 0,
      height: 'inherit',
      textAlign: 'center'
    },
    '&:focus-within': {
      outline: `1px solid ${mixins.amethyst}`,
      marginLeft: 2,

      '& .MuiInputBase-root': {
        color: mixins.color_text_primary
      }
    },

    [mixins['md-sm-min']]: {
      maxWidth: '80px'
    }
  }
});

export const CourseHeaderBookActionsContainer = styled('div')({
  '&&': {
    display: 'flex',
    gap: '12px',
    width: '100%',

    [mixins['md-sm-min']]: {
      height: '100%',
      alignItems: 'flex-end'
    }
  }
});

export const CourseHeaderBookActions = styled('div')({
  '&&': {
    display: 'flex',
    width: '100%',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    flexGrow: 1,
    gap: 12,
    flexDirection: 'column',

    [mixins['md-sm-min']]: {
      flexDirection: 'row'
    }
  }
});

export const CourseHeaderScheduleContainer = styled('div')({
  '&&': {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  }
});

export const CourseHeaderScheduleTitle = styled(Typography)({
  '&&': {
    color: mixins.zambezi,
    fontFeatureSettings: '\'liga\' off',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: 12,
    fontStyle: 'normal',
    fontWeight: mixins.fontWeight400,
    lineHeight: '20px'
  }
});

export const CourseHeaderScheduleItem = styled('div')({
  '&&': {
    display: 'flex'
  }
});

export const CourseHeaderScheduleItemTitle = styled(Typography)({
  '&&': {
    display: 'flex',
    padding: '4px 8px',
    color: mixins.color_text_primary,
    fontFeatureSettings: '\'liga\' off',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: 12,
    fontStyle: 'normal',
    fontWeight: 700,
    lineHeight: '20px',
    whiteSpace: 'nowrap',
    border: '1px solid rgba(13, 0, 77, 0.15)',
    borderRadius: 4,
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0
  }
});

export const CourseHeaderScheduleItemsContainer = styled('div')({
  '&&': {
    display: 'flex',
    gap: 8,
    flexWrap: 'wrap'
  }
});

export const CourseHeaderScheduleItemTime = styled(Typography)({
  '&&': {
    display: 'flex',
    padding: '4px 8px',
    color: mixins.color_text_primary,
    fontFeatureSettings: '\'liga\' off',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: 12,
    fontStyle: 'normal',
    fontWeight: 700,
    lineHeight: '20px',
    whiteSpace: 'nowrap',
    border: '1px solid rgba(13, 0, 77, 0.15)',
    borderRadius: 4,
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    borderLeftWidth: 0
  }
});

export const CourseHeaderTitleInfoContainer = styled('div')({
  '&&': {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
    width: '100%',
    justifyContent: 'space-between'
  }
});

export const CourseHeaderCourseMenuIcon = styled(CourseHeaderIconButton, {
  shouldForwardProp: (prop) => prop !== 'isMenuOpen'
})(({ isMenuOpen }) => ({
  '&&': {
    padding: 4,
    border: 'none',
    width: 24,
    height: 24,
    transition: 'transform 0.3s ease-in-out',
    marginLeft: 'auto',
    justifyContent: 'flex-end',
    transform: isMenuOpen ? 'rotate(-90deg)' : undefined,

    [mixins['md-sm-min']]: {
      marginTop: 6
    },

    '& > span, & svg': {
      width: 16,
      height: 16
    }
  }
}));

export const CourseHeaderMenuPaper = styled(Paper)({
  '&&': {
    position: 'absolute',
    width: 240,
    minWidth: 240,
    maxWidth: 'calc(100% - 32px)',
    outline: 0,
    backgroundColor: mixins.chalkWhiteFont,
    opacity: 1,
    borderRadius: 8,
    marginTop: 0,
    overflow: 'visible',
    boxShadow: '0px 5px 5px -3px rgba(0, 0, 0, 0.2), 0px 8px 10px 1px rgba(0, 0, 0, 0.14), 0px 3px 14px 2px rgba(0, 0, 0, 0.12)',
    boxSizing: 'border-box',

    '& *': {
      boxSizing: 'border-box'
    }
  }
});

export const CourseHeaderMenuList = styled(MenuList)({
  '&&': {
    padding: '8px !important',
    width: '100%',
    boxSizing: 'border-box'
  }
});

export const CourseHeaderMenuItem = styled(MenuItem, {
  shouldForwardProp: (prop) => prop !== 'hasDivider'
})(({ hasDivider }) => ({
  '&&': {
    padding: '8px !important',
    minHeight: 'unset',
    width: '100%',
    boxSizing: 'border-box',
    ...(hasDivider ? { borderTop: `1px solid ${mixins.color_elevation_alpha_12}` } : {}),

    '& p': {
      color: mixins.timeIconColor,
      fontFeatureSettings: '\'liga\' off',
      fontFamily: mixins.fontFamilyPlusSans,
      fontSize: mixins.fontSize14,
      fontStyle: 'normal',
      fontWeight: mixins.fontWeight400,
      lineHeight: '22px'
    },

    '&:hover': {
      backgroundColor: mixins.quaternary
    }
  }
}));

export const CourseHeaderIcon = styled(Icon)({
  '&&': {
    width: 16,
    height: 16,
    fontSize: 16,
    display: 'inline-flex',

    '& .MuiSvgIcon-root': {
      width: 16,
      height: 16,
      fontSize: 16
    }
  }
});

export const CourseHeaderIconCtaButton = styled(Button)({
  '&&': {
    color: mixins.navyColor,
    fontFeatureSettings: '\'liga\' off',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize14,
    fontStyle: 'normal',
    fontWeight: mixins.fontWeight500,
    lineHeight: '24px',
    padding: '8px 16px 10px 12px',
    maxHeight: 40,
    borderRadius: '1000px',
    minWidth: 'unset',
    height: 'fit-content',
    whiteSpace: 'nowrap',
    textTransform: 'none',
    border: 'none',

    '&:hover': {
      background: mixins.secondaryButtonHoverColor
    },
    '& .MuiButton-startIcon': {
      marginRight: 4,
      marginTop: 1,
      marginLeft: 0,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    '& .MuiSvgIcon-root': {
      width: 16,
      height: 16,
      fontSize: 16
    },
    '&.Mui-disabled': {
      color: mixins.disabledTextColor,
      opacity: 'unset',
      pointerEvents: 'unset',

      '&:hover': {
        background: mixins.secondaryButtonHoverColor,
        color: 'inherit'
      }
    }
  }
});

export const CourseHeaderActionContainer = styled('div')({
  '&&': {
    display: 'flex',
    flexWrap: 'wrap',
    flexDirection: 'column',
    alignItems: 'flex-start',
    [mixins['md-sm-min']]: {
      width: '100%',
      justifyContent: 'space-between',
      flexWrap: 'nowrap',
      flexDirection: 'row',
      alignItems: 'unset'
    }
  }
});

export const CourseHeaderFooterContainer = styled('div')({
  '&&': {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
    alignItems: 'center',
    marginTop: 'auto',
    paddingTop: 0,

    '& .MuiIconButton-root': {
      width: 24,
      height: 24,
      minWidth: 24,
      padding: 4,
      borderRadius: '1000px',
      border: 'none',
      marginTop: 0,
      backgroundColor: 'transparent',

      '&:hover': {
        backgroundColor: mixins.secondaryButtonHoverColor
      },

      '& > span, & svg': {
        width: 16,
        height: 16
      }
    }
  }
});

export const CourseHeaderButton = styled(Button)({
  '&&': {}
});

export const CourseHeaderUnifiedRoot = styled(CourseHeaderCard)({
  '&&': {
    backgroundColor: mixins.lightThemeBgColor2,
    borderRadius: 24,
    padding: 16,
    gap: 16
  }
});

export const CourseHeaderUnifiedCard = styled('div')({
  '&&': {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    backgroundColor: mixins.chalkWhiteFont,
    borderRadius: 16,
    padding: 16,
    boxSizing: 'border-box'
  }
});

export const CourseHeaderUnifiedCardMain = styled('div')({
  '&&': {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    flex: '1 1 auto',

    [mixins['lg-min']]: {
      position: 'relative',
      minHeight: 128
    }
  }
});

export const CourseHeaderUnifiedCardBody = styled('div')({
  '&&': {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gridTemplateAreas: `
      "cover"
      "content"
      "actions"
    `,
    rowGap: 12,
    width: '100%',
    alignItems: 'start',

    [mixins['md-sm-min']]: {
      gridTemplateColumns: 'auto 1fr auto',
      gridTemplateAreas: 'none',
      columnGap: 24,
      rowGap: 8
    }
  }
});

export const CourseHeaderUnifiedCoverContainer = styled('div')({
  '&&': {
    position: 'relative',
    flexShrink: 0,
    width: 96,
    height: 128,
    gridArea: 'cover',
    alignSelf: 'start',
    overflow: 'visible',

    [mixins['md-sm-min']]: {
      gridArea: 'unset',
      gridColumn: '1',
      gridRow: '1'
    }
  }
});

export const CourseHeaderUnifiedCoverImage = styled('img')({
  '&&': {
    width: 96,
    height: 128,
    objectFit: 'cover',
    borderRadius: 4,
    boxShadow: '0px 1px 3px 0px rgba(0, 0, 0, 0.10), 0px 1px 2px -1px rgba(0, 0, 0, 0.10)',
    display: 'block'
  }
});

export const CourseHeaderUnifiedDownloadRoot = styled('div')({
  '&&': {
    width: 96,
    height: 128,
    borderRadius: 4,
    boxShadow: '0px 1px 3px 0px rgba(0, 0, 0, 0.10), 0px 1px 2px -1px rgba(0, 0, 0, 0.10)',

    '& > div': {
      width: '100%',
      height: '100%',
      borderRadius: 4
    }
  }
});

export const CourseHeaderUnifiedContent = styled('div')({
  '&&': {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    width: '100%',
    gridArea: 'content',
    alignSelf: 'start',
    minWidth: 0,

    [mixins['md-sm-min']]: {
      gridArea: 'unset',
      gridColumn: '2',
      gridRow: '1'
    }
  }
});

export const CourseHeaderUnifiedCourseTitle = styled(Typography)({
  '&&': {
    color: mixins.color_text_primary,
    fontFeatureSettings: '\'liga\' off',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: '24px',
    fontStyle: 'normal',
    fontWeight: mixins.fontWeight500,
    lineHeight: '32px',
    letterSpacing: '-0.48px',
    wordBreak: 'break-word',
    whiteSpace: 'normal',
    width: '100%',
    minWidth: 0,
    ...CommonStyles().truncateNumberOfLines(3),

    [mixins['lg-min']]: {
      display: 'block',
      overflow: 'visible',
      textOverflow: 'unset',
      WebkitBoxOrient: 'unset',
      WebkitLineClamp: 'unset',
      lineClamp: 'unset'
    }
  }
});

export const CourseHeaderUnifiedMetaRow = styled('div')({
  '&&': {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 8,
    paddingTop: 4,
    width: '100%',

    [mixins['md-sm-min']]: {
      flexWrap: 'nowrap',
      gap: 12,
      paddingTop: 8
    }
  }
});

export const CourseHeaderUnifiedBadge = styled('span')({
  '&&': {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '2px 8px',
    borderRadius: 4,
    backgroundColor: '#EFEDFF',
    color: mixins.amethyst,
    fontFeatureSettings: '\'liga\' off',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize16,
    fontStyle: 'normal',
    fontWeight: mixins.fontWeight500,
    lineHeight: '20px',
    whiteSpace: 'nowrap',
    flexShrink: 0
  }
});

export const CourseHeaderUnifiedDateRange = styled(Typography)({
  '&&': {
    color: mixins.greyScaleColor,
    fontFeatureSettings: '\'liga\' off',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize16,
    fontStyle: 'normal',
    fontWeight: mixins.fontWeight500,
    lineHeight: '20px',
    whiteSpace: 'nowrap',
    flexShrink: 0
  }
});

export const CourseHeaderUnifiedBookTitle = styled(Typography)({
  '&&': {
    color: mixins.zambezi,
    fontFeatureSettings: '\'liga\' off',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize14,
    fontStyle: 'normal',
    fontWeight: mixins.fontWeight600,
    lineHeight: '16px',
    paddingTop: 16,
    width: '100%',
    minWidth: 0,
    wordBreak: 'break-word',
    whiteSpace: 'normal',
    ...CommonStyles().truncateNumberOfLines(3),

    [mixins['lg-min']]: {
      whiteSpace: 'normal',
      wordBreak: 'break-word',
      overflow: 'visible',
      textOverflow: 'unset'
    }
  }
});

export const CourseHeaderUnifiedActions = styled('div')({
  '&&': {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    alignItems: 'center',
    flexShrink: 0,
    gridArea: 'actions',
    width: '100%',
    justifySelf: 'stretch',
    justifyContent: 'flex-start',

    [mixins['md-sm-min']]: {
      gridArea: 'unset',
      gridColumn: '3',
      gridRow: '1',
      alignSelf: 'start',
      justifySelf: 'end',
      width: 'auto'
    }
  }
});

export const CourseHeaderUnifiedFooter = styled('div')({
  '&&': {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    width: '100%',
    marginTop: 'auto',
    paddingTop: 0,
    flexShrink: 0,

    [mixins['lg-min']]: {
      position: 'absolute',
      right: 0,
      top: 128,
      transform: 'translateY(-100%)',
      width: 'auto',
      marginTop: 0,
      paddingTop: 0
    }
  }
});

